<template>
  <div
    :ref="`ascChips${_uid}`"
    :class="['asc-chips', compact ? 'asc-chips--compact' : null]"
  >
    <div v-el-resize class="asc-chip-group" @resize="handleChipsResize">
      <asc-chip
        v-for="item in items"
        :key="item.value"
        class="asc-chip-group__item"
        :value="item.value"
        :tooltip="item.text"
        :text="item.text"
        :on-close="handleChipClose"
        :disabled="disabled"
        :compact="compact"
        :close-hidden="closeHidden"
      ></asc-chip>
    </div>
    <div class="asc-chip-group__summary">
      <template v-if="displayChips">
        <span class="asc-chip-group__summary-ellipsis">...</span>
      </template>
      <asc-chip
        :text="getSummaryText"
        :tooltip="getSummaryTooltip"
        :on-close="handleSummaryChipClose"
        :disabled="disabled"
        :compact="compact"
        :close-hidden="closeHidden"
      ></asc-chip>
    </div>
  </div>
</template>

<script>
import AscChip from '@/components/atoms/AscChip'

export default {
  name: 'AscChips',

  components: {
    AscChip,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    closeHidden: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      visibleChipCount: null,
      selectedItems: [],
    }
  },

  computed: {
    displayChips() {
      return this.items.length > 0 && this.visibleChipCount !== 0
    },
    getSummaryText() {
      return `(${this.items.length - this.visibleChipCount})`
    },
    getSummaryTooltip() {
      const hiddenCount = this.items.length - this.visibleChipCount
      return `${hiddenCount} items are selected but not shown`
    },
  },

  watch: {
    items: {
      handler: function (newValue) {
        this.items = newValue
        if (newValue !== this.items) {
          this.handleChipsResize()
        }
      },
      immediate: true,
    },
  },

  mounted() {
    window.addEventListener('resize', this.handleChipsResize)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleChipsResize)
  },

  methods: {
    handleChipClose(id) {
      if (this.onClose) {
        this.onClose(id)
      }
    },
    handleSummaryChipClose() {
      if (this.onClose) {
        const index = -Math.abs(this.visibleChipCount)
        this.onClose(index)
      }
    },
    handleChipsResize() {
      const chips = this.$refs[`ascChips${this._uid}`]

      this.visibleChipCount = null
      const chipGroup = chips.querySelectorAll('.asc-chip-group')[0]
      if (chipGroup) {
        const chipGroupRect = chipGroup.getBoundingClientRect()
        const chipGroupWidth = chipGroupRect.width
        const chipWidthOffset = chipGroupWidth
        const items = chipGroup.querySelectorAll('.asc-chip-group__item')
        let totalWidth = 0
        let hasMaxHit = false
        let summaryChipLeft = 0
        let visibleItemCount = 0
        for (let index = 0; index < items.length; index++) {
          if (!hasMaxHit) {
            const itemRect = items[index].getBoundingClientRect()
            if (itemRect) {
              const itemWidth = itemRect.width + 4
              if (totalWidth + itemWidth > chipWidthOffset) {
                hasMaxHit = true
                summaryChipLeft = totalWidth
              } else {
                visibleItemCount++
                totalWidth += itemWidth
              }
            }
          }
        }
        this.visibleChipCount = visibleItemCount
        const summaryChips = chips.querySelectorAll('.asc-chip-group__summary')
        if (summaryChips.length > 0) {
          const summaryChip = summaryChips[0]
          summaryChip.style.left = `${summaryChipLeft}px`
        }
        const summaryChip = summaryChips[0]
        if (this.items.length - this.visibleChipCount === 0) {
          summaryChip.style.display = 'none'
        } else {
          summaryChip.style.display = 'inline-flex'
        }
      }
    },
  },
}
</script>

<style lang="scss">
.asc-chips {
  position: relative;
  height: 22px;
  min-width: 70px;

  .asc-chip-group {
    display: inline-flex;
    flex-wrap: wrap;
    overflow: hidden;
    height: 22px;
    position: absolute;
    max-width: calc(100% - 76px);

    &__item {
      margin: 0 4px 0 0;
    }

    &__summary {
      display: inline-flex;
      position: absolute;
    }

    &__summary-ellipsis {
      display: block;
      padding-top: 6px;
      margin: 0 4px 0 2px;
    }
  }

  &--compact {
    .asc-chip-group {
      max-width: calc(100% - 52px);

      &__summary-ellipsis {
        margin: 0 3px 0 0;
      }
    }
  }
}
</style>
